import type { PlainMessage } from '@bufbuild/protobuf';
import {
  $isLineBreakNode,
  $isTextNode,
  type LexicalNode,
  type TextNode,
} from 'lexical';
import { omit } from 'lodash-es';

import type { DocumentTemplateIssue } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type { DocumentSectionSpec, NoteEditorBlockDef } from '../../config';
import {
  $isTemplateFieldNode,
  $isTemplateInstructionNode,
  $isTemplateParagraphNode,
  type TemplateHeadingNode,
  type TemplateLineDef,
  type TemplateParagraphNode,
} from '../../ui/TemplateEditor/nodes';
import { createTempId } from '../createTempId';

import {
  $listTemplateSections,
  $readTemplateLine,
  $readTemplateSection,
  $isSectionGuidance,
  $readTemplateWording,
  createTemplateLineDef,
  createTemplateTextDef,
  isEmptyTemplateText,
  readSectionGuidance,
  type TemplateDocumentIssueKind,
  type TemplateSectionNodes,
} from './templateDocument';

type TemplateIssue = PlainMessage<DocumentTemplateIssue>;

export type TemplateDocumentExport = {
  sections: DocumentSectionSpec[];
  issues: TemplateIssue[];
};

type ExportedBlock = {
  def: NoteEditorBlockDef;
  // A wording the author emptied: it goes while its line holds more.
  emptied: boolean;
};

type Exported<T> = { value: T; issues: TemplateIssue[] };

const createIssue = (
  Kind: TemplateDocumentIssueKind,
  place: Partial<Pick<TemplateIssue, 'SectionID' | 'LineID' | 'BlockID'>>,
): TemplateIssue => ({
  Kind,
  SectionID: '',
  LineID: '',
  BlockID: '',
  Message: '',
  ...place,
});

const withText = (
  def: NoteEditorBlockDef,
  Value: string,
): NoteEditorBlockDef =>
  def.Kind.case === 'Text'
    ? { ...def, Kind: { case: 'Text', value: { ...def.Kind.value, Value } } }
    : createTemplateTextDef(def.ID, Value);

const blockIdOf = (node: TextNode) =>
  $readTemplateWording(node)?.def.ID ?? null;

// Consecutive text of one text block is one group; every other node is a
// group of its own.
const groupLineChildren = (children: LexicalNode[]) =>
  children.reduce<LexicalNode[][]>((groups, node) => {
    const last = groups.at(-1);
    const joins =
      !!last &&
      $isTextNode(node) &&
      $isTextNode(last[0]) &&
      blockIdOf(last[0]) === blockIdOf(node);
    return joins
      ? [...groups.slice(0, -1), [...last, node]]
      : [...groups, [node]];
  }, []);

// Text no block owns is the space between two fields, which is not saved.
const exportText = (nodes: TextNode[]): ExportedBlock[] => {
  const def = $readTemplateWording(nodes[0])?.def;
  const value = nodes
    .map((node) => node.getTextContent())
    .join('')
    .trim();
  if (!def) {
    return value
      ? [{ def: createTemplateTextDef(createTempId(), value), emptied: false }]
      : [];
  }
  const original = def.Kind.case === 'Text' ? def.Kind.value.Value : '';
  return [{ def: withText(def, value), emptied: !value && !!original }];
};

const exportLineGroup = (
  nodes: LexicalNode[],
  LineID: string,
): Exported<ExportedBlock[]> => {
  const [node] = nodes;
  if ($isTextNode(node)) {
    return { value: exportText(nodes as TextNode[]), issues: [] };
  }
  if ($isSectionGuidance(node)) {
    return { value: [], issues: [] };
  }
  if ($isTemplateFieldNode(node) || $isTemplateInstructionNode(node)) {
    return { value: [{ def: node.getDef(), emptied: false }], issues: [] };
  }
  return {
    value: [],
    issues: [
      createIssue(
        $isLineBreakNode(node) ? 'line_break_in_line' : 'unknown_node',
        { LineID },
      ),
    ],
  };
};

// The empty text blocks a line was read with go back after the block each
// followed, or last when that block is gone.
const withEmptyTexts = (
  blocks: NoteEditorBlockDef[],
  read: NoteEditorBlockDef[],
) =>
  read.reduce((done, def, index) => {
    if (
      !isEmptyTemplateText(def) ||
      done.some((block) => block.ID === def.ID)
    ) {
      return done;
    }
    const after = index ? read[index - 1].ID : null;
    const at =
      after === null ? 0 : done.findIndex((block) => block.ID === after) + 1;
    return after !== null && at === 0
      ? [...done, def]
      : [...done.slice(0, at), def, ...done.slice(at)];
  }, blocks);

const exportLine = (
  paragraph: TemplateParagraphNode,
): Exported<TemplateLineDef> => {
  const line = $readTemplateLine(paragraph) ?? createTemplateLineDef();
  const read = groupLineChildren(paragraph.getChildren()).map((group) =>
    exportLineGroup(group, line.ID),
  );
  const blocks = read.flatMap((part) => part.value);
  const kept =
    blocks.length > 1 ? blocks.filter((block) => !block.emptied) : blocks;
  return {
    value: {
      ...line,
      Blocks: withEmptyTexts(
        kept.map((block) => block.def),
        line.Blocks,
      ),
    },
    issues: read.flatMap((part) => part.issues),
  };
};

const createSectionDefaults = (): DocumentSectionSpec => ({
  ID: createTempId(),
  Code: '',
  Title: '',
  EditorType: '',
  Type: '',
  ProcessingType: '',
  Options: [],
  TemplateType: '',
  Required: false,
  Lines: [],
});

const findSectionGuidance = (body: LexicalNode[]) =>
  body
    .filter($isTemplateParagraphNode)
    .flatMap((paragraph) => paragraph.getChildren())
    .find($isSectionGuidance);

// A line that holds its section's guidance alone is no line of the section.
const isGuidanceLine = (paragraph: TemplateParagraphNode) => {
  const children = paragraph.getChildren();
  return (
    children.some($isSectionGuidance) &&
    children.every(
      (child) =>
        $isSectionGuidance(child) ||
        ($isTextNode(child) && !child.getTextContent().trim()),
    )
  );
};

// A field's instruction is its guidance under its heading, or else the text
// typed there, a paragraph a line of it. One read as empty stays empty; one
// read as absent stays absent.
const readInstructions = (body: LexicalNode[], stored: DocumentSectionSpec) => {
  const guidance = findSectionGuidance(body);
  if (guidance) {
    return readSectionGuidance(guidance, stored.Instructions);
  }
  const instructions = body
    .filter($isTemplateParagraphNode)
    .map((paragraph) => paragraph.getTextContent())
    .join('\n')
    .trim();
  return instructions || (stored.Instructions === '' ? '' : undefined);
};

// Instructions a section was read without stay absent from it, and the
// section keeps the order of its keys.
const withInstructions = (
  section: DocumentSectionSpec,
  Instructions: string | undefined,
): DocumentSectionSpec => {
  if (Instructions !== undefined) {
    return { ...section, Instructions };
  }
  return omit(section, 'Instructions');
};

const exportSection = (
  heading: TemplateHeadingNode,
  body: LexicalNode[],
  fields: boolean,
): Exported<DocumentSectionSpec> => {
  const stored = $readTemplateSection(heading) ?? createSectionDefaults();
  const SectionID = stored.ID;
  const lines = fields
    ? []
    : body
        .filter($isTemplateParagraphNode)
        .filter((paragraph) => !isGuidanceLine(paragraph))
        .map(exportLine);
  const unknown = body.filter((node) => !$isTemplateParagraphNode(node));
  return {
    value: {
      ...withInstructions(
        stored,
        fields
          ? readInstructions(body, stored)
          : readSectionGuidance(findSectionGuidance(body), stored.Instructions),
      ),
      Title: heading.getTextContent().trim(),
      Lines: lines.map((line) => line.value),
    },
    issues: [
      ...unknown.map(() => createIssue('unknown_node', { SectionID })),
      ...lines.flatMap((line) => line.issues),
    ],
  };
};

type IdPlace = {
  id: string;
  place: Partial<Pick<TemplateIssue, 'SectionID' | 'LineID' | 'BlockID'>>;
};

const listIdPlaces = (sections: DocumentSectionSpec[]): IdPlace[] =>
  sections.flatMap((section) => [
    { id: section.ID, place: { SectionID: section.ID } },
    ...section.Lines.flatMap((line) => [
      { id: line.ID, place: { LineID: line.ID } },
      ...line.Blocks.map((block) => ({
        id: block.ID,
        place: { LineID: line.ID, BlockID: block.ID },
      })),
    ]),
  ]);

const findDuplicateIds = (sections: DocumentSectionSpec[]) =>
  listIdPlaces(sections)
    .filter(
      ({ id }, index, places) =>
        places.findIndex((other) => other.id === id) !== index,
    )
    .map(({ place }) => createIssue('duplicate_id', place));

// What stands before the first heading belongs to no section; an empty line
// there is nothing.
const listContentBeforeSection = (sections: TemplateSectionNodes[]) =>
  sections
    .filter((section) => !section.heading)
    .flatMap((section) => section.body)
    .filter((node) => !$isTemplateParagraphNode(node) || !node.isEmpty())
    .map(() => createIssue('content_before_section', {}));

// The document as the template's sections: a heading and the paragraphs
// after it are a section and its lines (or, in `fields`, a field and its
// instruction); what the template cannot hold is returned as issues,
// placed where it is.
export const $exportTemplate = ({
  fields = false,
}: { fields?: boolean } = {}): TemplateDocumentExport => {
  const nodes = $listTemplateSections();
  const sections = nodes.flatMap(({ heading, body }) =>
    heading ? [exportSection(heading, body, fields)] : [],
  );
  const value = sections.map((section) => section.value);
  return {
    sections: value,
    issues: [
      ...listContentBeforeSection(nodes),
      ...sections.flatMap((section) => section.issues),
      ...findDuplicateIds(value),
    ],
  };
};
